/**
 * Surrender-years normalization, shared by every surface that reads a row's
 * surrender term: the FA grid filter, the FA option lists (rail picker + the
 * legacy L1/L2 chip), and the IA catalog enrichment.
 *
 * It exists because those surfaces each rolled their own parse and disagreed:
 * the option lists rounded (`Math.round(r.surrYears)`), the rail picker didn't
 * (`Number(v)`), and the filter rounded BOTH the row and the user's pick. That
 * made three separate defects possible:
 *
 *   · `Math.round(null)` is 0, not NaN — so a row carrying no surrender data at
 *     all was matched as "0 years" by the filter.
 *   · rounding both sides let a pick of 7 match a 7.4-year row (and a 6.5 pick
 *     match a 7-year row), because both collapsed to the same integer.
 *   · the feeds express a multi-term product as a CSV ("5, 7") — see
 *     `parseSurrYears` in the IA enrichment path, which has always split on
 *     comma. `Number("5, 7")` is NaN, so such a row was dropped from the option
 *     list and could never match a pick.
 *
 * One parse, one comparison, so the list a user picks from and the rows they
 * get back are derived the same way.
 */

/**
 * A row's surrender term(s) as a list of years, de-duplicated and ascending.
 *
 * Accepts what the feeds actually send: a number, a numeric string, a CSV of
 * either ("5, 7"), or null/'' for a row with no surrender data. Returns `[]`
 * for the last case — an empty list never matches a specific pick, which is
 * how the IA path already treats a row with no `surrYearsList`.
 *
 * Values are kept as given, NOT rounded: callers compare parsed against parsed,
 * so a fractional term matches only itself.
 */
export function parseSurrYearList(raw: unknown): number[] {
  if (raw == null || raw === '') return [];
  if (typeof raw === 'number') return Number.isFinite(raw) ? [raw] : [];
  const out = String(raw)
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n));
  return [...new Set(out)].sort((a, b) => a - b);
}

/**
 * True when a row's surrender term(s) include any of the picked years.
 *
 * `picked` is empty when the filter is off — callers guard on that themselves,
 * so this returns false for an empty pick list rather than "matches
 * everything"; that keeps the meaning of a call unambiguous at the call site.
 */
export function surrYearsMatch(raw: unknown, picked: readonly number[]): boolean {
  if (picked.length === 0) return false;
  const yrs = parseSurrYearList(raw);
  if (yrs.length === 0) return false;
  const want = new Set(picked);
  return yrs.some((y) => want.has(y));
}
